import { useMemo } from "react"
import { CardElement } from "@stripe/react-stripe-js"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { Label } from "~/react/ui/label"
import { FormHelper } from "~/helpers/form_helper"
import { STRIPE_CARD_FONT_SIZE } from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"

import visaLogo from "~/images/payment_cards/visa.svg"
import mastercardLogo from "~/images/payment_cards/mastercard.png"
import amexLogo from "~/images/payment_cards/amex.png"

type CardFormDisplayProps = {
  cardholderName: string
  setCardholderName: (name: string) => void
  onCancel: () => void
  showBackButton: boolean
  showCardElement?: boolean
}

export function CardFormDisplay({
  cardholderName,
  setCardholderName,
  onCancel,
  showBackButton,
  showCardElement = false,
}: CardFormDisplayProps) {
  // Get Stripe element styles using CSS variables for consistent theming
  const cardElementOptions = useMemo(
    () => FormHelper.elementStyles(STRIPE_CARD_FONT_SIZE),
    [],
  )

  return (
    <div className="new-card-form">
      {showBackButton && (
        <div className="mb-3">
          <Button
            variant="linkGrey"
            size="sm"
            type="button"
            onClick={onCancel}
            className="p-0"
          >
            <Icon icon="arrow-left" size="sm" />
            {i18n.t("js.email_accounts_center.add_slots.use_existing_card")}
          </Button>
        </div>
      )}

      <Label htmlFor="cardholder-name" className="mb-1">
        {i18n.t("js.email_accounts_center.add_slots.cardholder_name")}
      </Label>
      <Input
        fullWidth
        id="cardholder-name"
        type="text"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
        required
        autoComplete="cc-name"
      />

      <fieldset className="form-group mt-4">
        <legend className="h-label">
          {i18n.t("js.email_accounts_center.add_slots.credit_card")}
        </legend>
        <div className="stripe-card-field mt-2">
          {showCardElement ? (
            <div id="card-field">
              <CardElement options={cardElementOptions} />
            </div>
          ) : (
            <div className="text-hint text-sm">
              {i18n.t("js.email_accounts_center.add_slots.stripe_loading")}
            </div>
          )}
        </div>
        <div className="text-2xs text-hint mt-2 d-flex align-items-center gap-1">
          <Icon icon="lock-alt" variant="regular" size="xs" />
          {i18n.t("js.email_accounts_center.add_slots.secured_and_encrypted")}
        </div>
      </fieldset>
    </div>
  )
}

type CardLogoProps = {
  cardType: string | undefined
}

export function CardLogo({ cardType }: CardLogoProps) {
  const type = cardType?.toLowerCase()

  if (type === "visa") {
    return <img src={visaLogo} alt="Visa" className="card-logo" />
  }
  if (type === "mastercard") {
    return <img src={mastercardLogo} alt="Mastercard" className="card-logo" />
  }
  if (
    type === "amex" ||
    type === "american express" ||
    type === "american_express"
  ) {
    return <img src={amexLogo} alt="American Express" className="card-logo" />
  }

  return (
    <span className="card-logo-fallback text-xs-medium">
      {cardType?.slice(0, 4) || "Card"}
    </span>
  )
}
